import React, { useEffect, useState } from 'react';
import BlockRenderer from './blocks/BlockRenderer';
import seed from './seed.json';

const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CMS_API_BASE) ||
  'http://localhost:8000';

function findSeedPage(slug) {
  return (seed.pages || []).find((p) => p.slug === slug) || null;
}

/**
 * PublicPage
 * ----------
 * Fetches a CMS page by slug and renders it via BlockRenderer in view mode.
 * Falls back to the static seed when the API is unreachable or returns 404
 * — so the website keeps working before the DB is seeded.
 */
function PublicPage({ slug }) {
  const [blocks, setBlocks] = useState(() => findSeedPage(slug)?.blocks || []);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/website-cms/pages/${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const remoteBlocks = json?.blocks || json?.data?.blocks || null;
        if (!cancelled && Array.isArray(remoteBlocks) && remoteBlocks.length) {
          setBlocks(remoteBlocks);
          setError(null);
          return;
        }
        throw new Error('Empty payload');
      } catch (err) {
        // Use seed fallback (already initialised in useState).
        if (!cancelled) {
          const fallback = findSeedPage(slug)?.blocks || [];
          setBlocks(fallback);
          setError(err);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [slug]);

  if (!blocks.length) {
    return (
      <div className="container-page py-32 text-center">
        <h1 className="font-display text-3xl text-olive-deep">Page not found</h1>
        <p className="mt-3 text-ink/60">No content available for "{slug}".</p>
        {error && <p className="mt-2 text-xs text-ink/40">{String(error.message || error)}</p>}
      </div>
    );
  }

  return <BlockRenderer blocks={blocks} editing={false} />;
}

export default PublicPage;
